import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import { getCompanyId } from "@lhd/utility";
import { notification } from "antd";
import { TTreeOrgsChart } from "types/tree.type";

export const useCreateOrUpdateOrganizationChart = () => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload, deleteIds, dateVersion, status, id, isUserDrag }: any) =>
      axios(
        id
          ? {
              method: "post",
              url: `${SERVICES.ORG_CHARTS}/tree`,
              data: {
                org_chart_nodes_data: {
                  data: payload,
                  delete_ids: deleteIds,
                },
                status,
                date_version: dateVersion,
                company_id: companyId,
                id,
                is_user_drag: isUserDrag,
              },
            }
          : {
              method: "post",
              url: `${SERVICES.ORG_CHARTS}/tree`,
              data: {
                org_chart_nodes_data: {
                  data: payload,
                  delete_ids: deleteIds,
                },
                status,
                date_version: dateVersion,
                company_id: companyId,
                is_user_drag: isUserDrag,
              },
            },
      ),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.ORGS_CHART]);
      },
      onError(e: any) {
        notification["error"]({
          message: e?.response?.data?.msg || "保存に失敗しました。",
        });
      },
    },
  );
};

export const useFetchOrgsChart = (
  page?: number,
  pageSize?: number,
  filterParams?: any,
  enabled = true,
) => {
  const companyId = getCompanyId();

  // Check if any filter has __NOT_FOUND__ (uncheckall state)
  const hasNullFilter = filterParams
    ? Object.values(filterParams).some((value: any) => {
        if (Array.isArray(value)) {
          return value.includes("__NOT_FOUND__");
        }
        return value === "__NOT_FOUND__";
      })
    : false;

  return useQuery(
    [QUERY_NAME.ORGS_CHART, companyId, filterParams, page, pageSize],
    () => {
      // Return empty data if __NOT_FOUND__ filter is present
      if (hasNullFilter) {
        return Promise.resolve({
          data: [] as TTreeOrgsChart[],
          meta: {
            total: 0,
            total_page: 0,
          },
        });
      }

      const params = {
        page: page || 1,
        per_page: pageSize || 100,
        "q[company_id_eq]": companyId,
        ...filterParams,
      };
      return axios({
        method: "get",
        url: `${SERVICES.ORG_CHARTS}`,
        params,
      }).then((res: any) => ({
        data: res.data as TTreeOrgsChart[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: enabled,
    },
  );
};

export const useFetchAllOrgsChart = (
  page: number,
  pageSize: number,
  enabled: boolean,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.ALL_ORG_FOR_CHART, companyId, page, pageSize],
    () => {
      const params = {
        page: page,
        per_page: pageSize,
        "q[company_id_eq]": companyId,
      };
      return axios({
        method: "get",
        url: `${SERVICES.ORG_CHARTS}`,
        params,
      }).then((res: any) => ({
        data: res.data as TTreeOrgsChart[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      }));
    },
    {
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: enabled,
    },
  );
};

export const useFetchOrgChartDetail = (id?: number | string) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.ORGS_CHART, id, companyId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.ORG_CHARTS}/${id}`,
        params: {
          company_id: companyId,
        },
      }).then((res) => (res.data as TTreeOrgsChart) || {}),
    {
      staleTime: 60000,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    },
  );
};

export const useDeleteMultipleOrgCharts = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ listIds }: any) => {
      const formData = new FormData();

      // Append each element if backend expects array format
      listIds.forEach((id: any) => {
        formData.append("delete_ids[]", id);
      });
      formData.append("q[company_id_eq]", getCompanyId());

      return axios({
        method: "post",
        url: `${SERVICES.ORG_CHARTS}/delete_multiple`,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    {
      onSuccess(data, variables: any) {
        queryClient.invalidateQueries([QUERY_NAME.ORGS_CHART], {
          refetchType: "active",
        });
      },
      onError(error: any) {
        notification.error({
          message: error.response.data.msg || "削除に失敗しました。",
          style: { whiteSpace: "pre-wrap", width: 500 },
        });
      },
    },
  );
};
export const useFetchOrgChartFilterOptions = (
  params: {
    company_id: string | number;
    field_key?: string;
    active_filters?: Record<string, string[]>;
    filter_order?: string[];
  },
  enabled?: boolean,
) => {
  return useQuery(
    ["org_chart_filter_options", params],
    () =>
      axios({
        method: "post",
        url: `${SERVICES.ORG_CHARTS}/filter_options`,
        data: {
          company_id: params.company_id,
          field_key: params.field_key,
          active_filters: params.active_filters || {},
          filter_order: params.filter_order || [],
        },
      }).then((res) => res.data),
    {
      enabled: enabled !== false,
      staleTime: 60000,
      cacheTime: 60000,
    },
  );
};
